/**
 * kbTypes — forma dei documenti della biblioteca unificata (`kb_contents`).
 *
 * Modulo PURO e CONDIVISO (`shared/`): nessun accesso a Firestore, nessuno stato,
 * nessuna dipendenza dagli SDK. Vive qui perché la STESSA forma deve valere per
 * la console (che scrive), le Cloud Functions (che derivano e pubblicano) e
 * l'app (che legge). Se le tre divergessero, il canale novità mostrerebbe cose
 * diverse da quelle pubblicate.
 * (Le functions lo ricevono via `npm run sync:shared` → `src/_shared-pkg/kb/`.)
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 *
 * ⚠️ TIMESTAMP — i due SDK Firebase hanno tipi `Timestamp` incompatibili fra
 * loro (client vs admin) e un modulo puro non può importarne nessuno. Qui i
 * campi temporali sono dichiarati `KbIstanteLike` (forma STRUTTURALE: qualunque
 * cosa sappia dire i propri millisecondi) e i confronti passano da `aMillis()`.
 * Nessun calcolo puro dipende dal tipo concreto.
 */

// ============================================================================
// TEMPO
// ============================================================================

/** Forma minima di un istante: qualunque Timestamp dei due SDK la soddisfa. */
export interface KbIstante {
  toMillis(): number;
}

/** Un campo temporale come può arrivare da Firestore, da una Date o da un numero. */
export type KbIstanteLike = KbIstante | Date | number | null | undefined;

/**
 * Millisecondi epoch di un istante, o `null` se il valore è assente o inutilizzabile.
 *
 * NON inventa un fallback (es. "adesso"): un istante mancante è `null` e chi
 * chiama decide cosa farne. Un default silenzioso qui produrrebbe scadenze e
 * ordinamenti sbagliati senza che nessuno se ne accorga.
 */
export function aMillis(valore: KbIstanteLike): number | null {
  if (valore == null) return null;
  if (typeof valore === 'number') return Number.isFinite(valore) ? valore : null;
  if (valore instanceof Date) {
    const ms = valore.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof (valore as KbIstante).toMillis === 'function') {
    const ms = (valore as KbIstante).toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

// ============================================================================
// VOCABOLARI (le union canoniche; le costanti runtime stanno in kbContentModel)
// ============================================================================

/** A chi appartiene il contenuto. IMMUTABILE dopo la creazione (imposto nelle rules). */
export type KbScope = 'piattaforma' | 'azienda';

/** Tipo di contenuto. Solo tre: un quarto formato è un valore + un renderer. */
export type KbFormato = 'articolo' | 'video' | 'novita';

/** Ciclo di vita editoriale. */
export type KbStato = 'bozza' | 'in_revisione' | 'pubblicato' | 'archiviato';

/**
 * Quanto il contenuto è allineato al gestionale di oggi.
 * Stesso vocabolario dei video (`VideoDocStato`), non un terzo elenco:
 * `da_aggiornare` resta VISIBILE con avviso, `obsoleto` non si mostra mai.
 */
export type KbFreschezza = 'attuale' | 'da_aggiornare' | 'obsoleto';

/** Peso dell'annuncio. Il comportamento associato vive in `kb_settings/governance`. */
export type KbLivelloImpatto = 'informativo' | 'rilevante' | 'importante' | 'critico';

/** Canali su cui l'annuncio può viaggiare. In fase 1 solo `in_app`. */
export type KbCanale = 'in_app' | 'email' | 'whatsapp' | 'display_monitor';

/** Come si decide quali tenant vedono il contenuto. */
export type KbModalitaTenant = 'tutti' | 'inclusi' | 'esclusi';

/** Natura della modifica: la decide l'umano, il sistema la propone. */
export type KbTipoModifica = 'maggiore' | 'minore';

/** Stato di una singola revisione. */
export type KbStatoRevisione = 'bozza' | 'pubblicata' | 'superata';

// ============================================================================
// VISIBILITÀ
// ============================================================================

/**
 * Visibilità dichiarativa per ruolo, come la scrive l'editor.
 * `audience_keys` (derivato dal trigger) è ciò che le query interrogano.
 */
export interface KbAudience {
  tutti_i_ruoli: boolean;
  /** Ruoli ammessi quando `tutti_i_ruoli` è false. Nomi da `src/core/rbac`. */
  ruoli: string[];
}

/**
 * Filtro di RILEVANZA per tenant e piano — **non** un confine di sicurezza:
 * l'unico confine imposto dal database è lo `scope` (vedi le rules).
 *
 * `capability_richiesta` NASCONDE (uso eccezionale, es. una beta riservata).
 * `capability_informativa` NON nasconde nulla: il contenuto si legge comunque e
 * la UI aggiunge una nota tipo «disponibile nel piano Business». È la scelta di
 * prodotto del 25/07/2026: **la documentazione del gestionale è sempre visibile
 * a qualsiasi azienda**, indipendentemente dai pacchetti acquistati.
 */
export interface KbVisibilitaTenant {
  modalita: KbModalitaTenant;
  tenant_ids: string[];
  capability_richiesta: string | null;
  capability_informativa: string | null;
}

// ============================================================================
// ANNUNCIO
// ============================================================================

/** Presente SOLO quando `formato === 'novita'`. */
export interface KbAnnuncio {
  livello_impatto: KbLivelloImpatto;
  canali: KbCanale[];
  data_annuncio: KbIstanteLike;
  /** Oltre questa data l'annuncio esce dall'evidenza ma resta nell'archivio. */
  scadenza_evidenza_at: KbIstanteLike;
  /** Slug di altri `kb_contents` da leggere per approfondire. */
  contenuti_correlati: string[];
  /** Rotta interna dove la novità vive davvero (es. `/preventivi/nuovo`). */
  rotta_app: string | null;
  /** Testo del bottone (es. «apri un preventivo»). Senza rotta non si mostra. */
  etichetta_azione: string | null;
  /** Iniziativa di roadmap che ha generato l'annuncio, se c'è. */
  iniziativa_id: string | null;
  /** Riferimento di rilascio leggibile (es. `2026.07`). */
  release_ref: string | null;
}

// ============================================================================
// DOCUMENTI
// ============================================================================

/** Video allegato al contenuto (assorbe integralmente il vecchio `PlatformDocsVideo`). */
export interface KbVideo {
  storage_path: string;
  durata_secondi?: number;
  poster_storage_path?: string;
  versione_app?: string;
}

export interface KbAllegato {
  storage_path: string;
  titolo: string;
  mime_type: string;
  dimensione_byte: number;
}

/**
 * `kb_contents/{slug}` — la TESTA del contenuto.
 *
 * Doc id = slug parlante (`noleggio-operativo-come-funziona`): la documentazione
 * è la cosa che gli utenti si incollano in chat, gli URL opachi si perdono.
 *
 * ⚠️ Il CORPO non è qui: vive in `kb_revisioni/{numero}`. Listare 20 contenuti
 * deve costare 20 testate leggere, non 20 corpi markdown.
 */
export interface KbContenuto {
  /** Slug = doc id. Presente sull'oggetto per comodità dei chiamanti. */
  id: string;

  // — identità e proprietà —
  scope: KbScope;
  /** Presente SOLO se `scope === 'azienda'`. Assente per la piattaforma. */
  tenant_id?: string;
  shared_with_tenants?: string[];

  // — formato e corpo —
  formato: KbFormato;
  revisione_pubblicata: number | null;
  revisione_bozza: number | null;
  video?: KbVideo | null;
  allegati?: KbAllegato[];

  // — presentazione e classificazione —
  titolo: string;
  /** Ciò che si legge nel widget e nelle card. Un solo campo, non due. */
  riassunto: string;
  lingua: string;
  /** Id di `knowledge_categories`, dalla radice alla foglia. */
  taxonomy_path: string[];
  taxonomy_root_id?: string | null;
  taxonomy_leaf_id?: string | null;
  tags: string[];
  difficolta?: 1 | 2 | 3;
  /** Id di `kb_areas`: vocabolario unico, giunzione con il changelog. */
  aree_coperte: string[];

  // — visibilità —
  audience: KbAudience;
  /** DERIVATO dal trigger: `['*']` oppure `['ruolo:admin', …]`. */
  audience_keys?: string[];
  /**
   * DERIVATO dal trigger: i termini con cui il contenuto è cercabile (radici,
   * vedi `kbRicerca`). Sta qui insieme agli altri derivati perché chi tipizza
   * una testata come `KbContenuto` deve poterlo leggere senza ricorrere a `any`.
   */
  search_tokens?: string[];
  visibilita_tenant: KbVisibilitaTenant;

  // — stato e freschezza —
  stato: KbStato;
  stato_freschezza: KbFreschezza;
  verificato_at?: KbIstanteLike;
  verificato_da?: string | null;
  versione_app_verificata?: string | null;
  revisione_ogni_giorni?: number | null;
  revisione_scadenza_at?: KbIstanteLike;
  /** Responsabile umano del contenuto. Un contenuto senza owner invecchia. */
  owner_uid: string;

  // — versionamento —
  versione_maggiore: number;
  versione_minore: number;
  content_hash?: string | null;
  ultimo_tipo_modifica?: KbTipoModifica | null;
  motivo_ultima_modifica?: string | null;
  prima_pubblicazione_at?: KbIstanteLike;
  pubblicato_at?: KbIstanteLike;
  pubblicato_by?: string | null;

  // — ordinamento —
  /**
   * DERIVATO: `annuncio.data_annuncio` per le novità, `pubblicato_at` per il
   * resto. Chiave unica di ordinamento per tutte le liste, così una revisione
   * minore non ributta un contenuto in cima ad «aggiunti di recente».
   */
  ordinamento_at?: KbIstanteLike;

  // — annuncio —
  annuncio?: KbAnnuncio | null;

  // — audit —
  created_at?: KbIstanteLike;
  created_by?: string | null;
  updated_at?: KbIstanteLike;
  updated_by?: string | null;
  visualizzazioni?: number;
  ultima_visualizzazione?: KbIstanteLike;
}

/**
 * `kb_contents/{slug}/kb_revisioni/{numero}` — il CORPO, immutabile.
 *
 * Doc id = numero zero-paddato (`'000007'`) → ordinamento lessicale senza indici.
 * `scope` e `tenant_id` sono denormalizzati perché le rules di sottocollezione
 * non possono leggere il padre senza pagare una `get()`.
 * Scritta SOLO da Cloud Function: nelle rules `allow write: if false`.
 */
export interface KbRevisione {
  numero: number;
  versione_maggiore: number;
  versione_minore: number;
  content_hash: string;
  titolo_snapshot: string;
  riassunto_snapshot: string;
  corpo_markdown: string;
  tipo_modifica: KbTipoModifica;
  motivo: string;
  stato_revisione: KbStatoRevisione;
  autore_uid: string;
  created_at?: KbIstanteLike;
  scope: KbScope;
  tenant_id?: string;
}

/** `kb_areas/{slug}` — vocabolario unico delle aree funzionali. */
export interface KbArea {
  id: string;
  label: string;
  descrizione?: string;
  modulo_id?: string | null;
  capability_id?: string | null;
  parent_area_id?: string | null;
  attivo: boolean;
}

/**
 * `kb_novita_stato/{user_id}__{content_id}` — stato di lettura di un annuncio.
 *
 * Tre timestamp DISTINTI, di proposito: «l'ho chiuso» non è «l'ho letto» e non è
 * «ho capito e confermo». Collassarli in un booleano perderebbe l'unica
 * informazione che serve per gli annunci importanti.
 */
export interface KbStatoLetturaNovita {
  id: string;
  user_id: string;
  content_id: string;
  tenant_id: string;
  letto_at?: KbIstanteLike;
  presa_visione_at?: KbIstanteLike;
  chiuso_at?: KbIstanteLike;
  origine?: 'widget' | 'centro' | 'link';
}

/** `kb_settings/governance` — comportamento configurabile, non hardcoded. */
export interface KbGovernance {
  giorni_validita_default: number;
  widget_max_novita: number;
  widget_mostra_se_vuoto: boolean;
  livelli_impatto: Record<
    KbLivelloImpatto,
    {
      ordine: number;
      label: string;
      /** L'annuncio resta in cima e non si chiude finché non si conferma. */
      forza_presa_visione: boolean;
      canali_default: KbCanale[];
      /** Giorni di permanenza in evidenza se l'editor non li specifica. */
      giorni_evidenza: number;
    }
  >;
}
